import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type {
  AddressForm,
  IndustryCode,
  Salutation,
} from 'features/conversation-preferences/services/conversation-preferences-service';

/** Onboarding progress per user. Persisted locally so a reload keeps it; the conversation
 *  preference holds the step and answers other devices resume from. */
export interface OnboardingProgress {
  /** Highest step answered or skipped; onboarding is done at ONBOARDING_TOTAL_STEPS. */
  finishedStep?: number;
  /** Local onboarding conversation created in step 1 — steps 2 and 3 continue in it. */
  conversationId?: string;
  /** The login count tells a new login apart from a page reload. */
  shownAtLoginCount?: number;
  /** The name Buddy uses: the given or preferred name. */
  name?: string;
  team?: string;
  tools?: string;
  completed?: boolean;
  /** Absent until chosen with a chip, said in the name answer, or defaulted to formal. English
   *  has a single form and never sets it. */
  addressForm?: AddressForm;
  givenName?: string;
  familyName?: string;
  salutation?: Salutation;
  /** The user's own salutation, e.g. "Dr.", when `salutation` is custom. */
  salutationText?: string;
  /** Guessed from the name; [Anrede ändern] lets the user correct it. */
  salutationInferred?: boolean;
  industry?: IndustryCode;
  /** The user's words when `industry` is other. */
  industryText?: string;
  /** The catch-up for users who finished the old onboarding is running. */
  catchUp?: boolean;
  /** Times the catch-up was put off with [Später]; it stops at 2. */
  catchUpSkips?: number;
}

interface OnboardingStoreState {
  completionSessionsByUser: Record<
    string,
    { revision: number; conversationsByTenant: Record<string, string> }
  >;
  beginCompletionSession: (
    userId: string,
    tenant: string
  ) => { revision: number; conversationId?: string };
  captureCompletionSession: (
    userId: string,
    tenant: string,
    revision: number,
    conversationId: string
  ) => void;
  clearCompletionSession: (userId: string) => void;
  progressByUser: Record<string, OnboardingProgress>;
  setProgress: (userId: string, progress: OnboardingProgress) => void;
}

export const useOnboardingStore = create<OnboardingStoreState>()(
  persist(
    (set, get) => ({
      completionSessionsByUser: {},
      beginCompletionSession: (userId, tenant) => {
        const previous = get().completionSessionsByUser[userId];
        const revision = (previous?.revision ?? 0) + 1;
        set((state) => ({
          completionSessionsByUser: {
            ...state.completionSessionsByUser,
            [userId]: { revision, conversationsByTenant: previous?.conversationsByTenant ?? {} },
          },
        }));
        return { revision, conversationId: previous?.conversationsByTenant[tenant] };
      },
      captureCompletionSession: (userId, tenant, revision, conversationId) => {
        set((state) => {
          const session = state.completionSessionsByUser[userId];
          if (
            !session ||
            session.revision !== revision ||
            (session.conversationsByTenant[tenant] &&
              session.conversationsByTenant[tenant] !== conversationId)
          )
            return state;
          return {
            completionSessionsByUser: {
              ...state.completionSessionsByUser,
              [userId]: {
                ...session,
                conversationsByTenant: {
                  ...session.conversationsByTenant,
                  [tenant]: conversationId,
                },
              },
            },
          };
        });
      },
      // Keep a tombstone: pending requests must never restore a completed session.
      clearCompletionSession: (userId) =>
        set((state) => ({
          completionSessionsByUser: {
            ...state.completionSessionsByUser,
            [userId]: {
              revision: (state.completionSessionsByUser[userId]?.revision ?? 0) + 1,
              conversationsByTenant: {},
            },
          },
        })),
      progressByUser: {},
      setProgress: (userId, progress) =>
        set((state) => ({ progressByUser: { ...state.progressByUser, [userId]: progress } })),
    }),
    { name: 'onboarding-storage' }
  )
);
